import { AssetMap } from './asset-map.js'
import { CanvasRepository } from './canvas-repository.js'
import { readStore, readAllStore, readStoreIndex, writeStore, deleteStore, commitGenerationDelivery } from '../storage/db.js'

// Shared-database adapter (account-scoped stores were cut with the isolation
// work): every method rides the module-level blaze-web-canvas database.
function sharedStore() {
  return Object.freeze({
    read: readStore,
    readAll: readAllStore,
    readIndex: readStoreIndex,
    write: writeStore,
    delete: deleteStore,
    commitGenerationDelivery: input => commitGenerationDelivery(input),
  })
}

function decodeBase64Png(value) {
  if (!value) return null
  const binary = atob(String(value).replace(/^data:image\/png;base64,/, ''))
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
  return new Blob([bytes], { type: 'image/png' })
}

function writeRichWithCopyEvent(text, html) {
  if (typeof document === 'undefined' || typeof document.execCommand !== 'function') return false
  let handled = false
  const onCopy = event => {
    if (!event.clipboardData) return
    event.clipboardData.setData('text/plain', text)
    event.clipboardData.setData('text/html', html)
    event.preventDefault()
    handled = true
  }
  document.addEventListener('copy', onCopy, true)
  try {
    return document.execCommand('copy') && handled
  } catch {
    return false
  } finally {
    document.removeEventListener('copy', onCopy, true)
  }
}

async function writeRichClipboard(text, html, pngBase64) {
  const plain = String(text ?? '')
  const rich = String(html ?? '')
  const clipboard = navigator.clipboard
  let richWriteError = null

  if (typeof clipboard?.write === 'function' && typeof ClipboardItem === 'function' && rich) {
    const parts = {
      'text/plain': new Blob([plain], { type: 'text/plain' }),
      'text/html': new Blob([rich], { type: 'text/html' }),
    }
    const png = decodeBase64Png(pngBase64)
    if (png) parts['image/png'] = png
    try {
      await clipboard.write([new ClipboardItem(parts)])
      return true
    } catch (error) {
      // Some Safari versions expose ClipboardItem but reject a multi-type item
      // containing PNG. Retry the Canvas marker without the external-app image
      // before falling back to plain text.
      if (png) {
        try {
          delete parts['image/png']
          await clipboard.write([new ClipboardItem(parts)])
          return true
        } catch {}
      }
      richWriteError = error
    }
  }

  // Safari/WebKit builds without ClipboardItem can still write multiple MIME
  // types synchronously while handling the user's copy shortcut.
  if (rich && writeRichWithCopyEvent(plain, rich)) return true
  if (typeof clipboard?.writeText !== 'function') throw richWriteError || new Error('clipboard_unavailable')
  await clipboard.writeText(plain)
  return false
}

function readOnlyError() {
  const error = new Error('environment_read_only')
  error.code = 'environment_read_only'
  return error
}

function lifecycleError(code) {
  const error = new Error(code)
  error.code = code
  return error
}

export function createWebCanvasHost(options = {}) {
  const readOnly = options.readOnly === true
  const store = options.store || sharedStore()
  let disposed = false
  let quiesced = false
  const genClients = new Set()
  const assertActive = () => {
    if (disposed) throw lifecycleError('web_canvas_host_disposed')
    if (quiesced) throw lifecycleError('web_canvas_host_quiesced')
  }
  const call = fn => (...args) => {
    assertActive()
    return fn(...args)
  }
  const denyWrite = () => Promise.reject(readOnlyError())
  const assets = new AssetMap({
    readOnly,
    read: (storeName, key) => store.read(storeName, key),
    write: (storeName, value, key) => store.write(storeName, value, key),
  })
  const repository = new CanvasRepository(assets, {
    readOnly,
    store,
    commitGenerationDelivery: input => store.commitGenerationDelivery(input),
  })
  const generationJobs = Object.freeze({
    read: call(id => store.read('gen_jobs', id)),
    readAll: call(() => store.readAll('gen_jobs')),
    readByClientJob: call(id => store.readIndex('gen_jobs', 'by_client_job', id)),
    write: call(row => store.write('gen_jobs', row)),
    delete: call(id => store.delete('gen_jobs', id)),
  })
  const host = {
    platform: 'web',
    readOnly,
    capabilities: Object.freeze({
      upload: !readOnly, download: true, video: true, model3d: false, mask: false,
      generation: !readOnly, agent: false, builtinActions: false, workflow: false,
      localVoice: false, localFiles: false, desktopIntegrations: false,
    }),
    canvases: {
      list: call(options => repository.list(options)), load: call((id, viewportSize) => repository.load(id, viewportSize)),
      attachPreviews: call(summaries => repository.attachPreviews(summaries)),
      stageLocal: call((id, document, version, viewportSize, options) => readOnly ? denyWrite() : repository.stageLocal(id, document, version, viewportSize, options)),
      save: call((id, document, version, viewportSize) => readOnly ? denyWrite() : repository.save(id, document, version, viewportSize)),
      flush: call(id => readOnly ? denyWrite() : repository.flush(id)),
      commitGenerationDelivery: call((id, input) => readOnly ? denyWrite() : repository.commitGenerationDelivery(id, input)),
      isGenerationDeliverySynced: call((id, delivery) => repository.isGenerationDeliverySynced(id, delivery)),
      flushView: call(id => readOnly ? denyWrite() : repository.flushView(id)),
      create: call(name => readOnly ? denyWrite() : repository.create(name)),
      rename: call((id, name) => readOnly ? denyWrite() : repository.rename(id, name)),
      duplicate: call((id, source) => readOnly ? denyWrite() : repository.duplicate(id, source)),
      delete: call(id => readOnly ? denyWrite() : repository.delete(id)),
      discardLocal: call(id => readOnly ? denyWrite() : repository.discardLocal(id)),
      resolveConflictKeepLocal: call(id => readOnly ? denyWrite() : repository.resolveConflictKeepLocal(id)),
      resumePending: call(() => readOnly ? Promise.resolve({ ok: true, readOnly: true }) : repository.resumePending()),
      subscribe: call((id, listener) => repository.subscribe(id, listener)),
    },
    assets: {
      prime: call(document => assets.prime(document)), resolve: call(uri => assets.resolve(uri)),
      resolveVariant: call((uri, kind) => assets.resolveVariant(uri, kind)),
      resolveFresh: call((uri, kind) => assets.resolveFresh(uri, kind)),
      resolveApiUrl: call(uri => assets.resolveApiUrl(uri)),
      refreshByUrl: call(url => assets.refreshByUrl(url)),
      resolveMedia: call(uri => assets.resolveMedia(uri)),
      resolveHashes: call(refs => assets.resolveHashes(refs)),
      poster: call(uri => assets.poster(uri)), resolvePoster: call(uri => assets.resolvePoster(uri)),
      warm: call(uri => assets.warm(uri)), resolveWarm: call(uri => assets.resolveWarm(uri)),
      importFile: call((file, importOptions) => readOnly ? denyWrite() : assets.importFile(file, importOptions)),
      commitPreparedImport: call(value => readOnly ? denyWrite() : assets.commitPreparedImport(value)),
      retry: call((uri, retryOptions) => readOnly ? denyWrite() : assets.retry(uri, retryOptions)),
      cancel: call(uri => readOnly ? denyWrite() : assets.cancel(uri)),
      download: call((uri, filename) => assets.download(uri, filename)), releaseAll: () => assets.releaseAll(),
    },
    clipboard: {
      writeText: text => navigator.clipboard.writeText(String(text)),
      writeRich: (text, html, pngBase64) => writeRichClipboard(text, html, pngBase64),
    },
    shell: {
      back: () => { location.href = '/canvases' },
      account: () => { location.href = '/canvases?account=1' },
    },
    generationJobs,
    // Page lifecycle escape hatch: quiesce() deliberately rejects every
    // public host operation, but the already-rendered document/view still
    // needs one final account-local IDB snapshot. CanvasRepository.stageLocal
    // has no remote I/O, and a quiesced repository refuses to schedule future
    // view/outbox workers.
    finalizeLocalSnapshot(id, document, version, viewportSize, snapshotOptions) {
      if (disposed) return Promise.reject(lifecycleError('web_canvas_host_disposed'))
      if (!quiesced) return Promise.reject(lifecycleError('web_canvas_host_not_quiesced'))
      if (readOnly) return denyWrite()
      return repository.finalizeLocalSnapshot(id, document, version, viewportSize, snapshotOptions)
    },
    registerGenClient(client) {
      assertActive()
      if (!client?.dispose) throw lifecycleError('web_canvas_gen_client_invalid')
      genClients.add(client)
      return client
    },
    // Stop every account-scoped source of external work synchronously while
    // leaving the IndexedDB handle open long enough for a pagehide snapshot
    // that was already enqueued to finish. A BFCache restore must never revive
    // this host; host-singleton replaces quiesced instances on the next lookup.
    quiesce() {
      if (disposed || quiesced) return false
      quiesced = true
      for (const client of genClients) {
        try { client.dispose() } catch {}
      }
      genClients.clear()
      try { repository.dispose() } catch {}
      try { assets.releaseAll() } catch {}
      return true
    },
    dispose() {
      if (disposed) return false
      host.quiesce()
      disposed = true
      try { store.close() } catch {}
      return true
    },
    get disposed() { return disposed },
    get quiesced() { return quiesced },
    _repository: repository,
    _assetMap: assets,
    _store: store,
  }
  return Object.freeze(host)
}
